import _ from 'lodash';
import { useDateFormat } from "@vueuse/core";
import DOMPurify from 'dompurify';

// ===============================
// FORMATTING UTILITIES
// ===============================


/**
 * Formats a date into 'MM/DD/YYYY' format.
 * @param {Date|string} date - The date to format.
 * @returns {string} The formatted date string.
 */
export const formatDate = (date) => {
    if (!date) return '';
    const formatted = useDateFormat(date, 'MM/DD/YYYY');
    return formatted.value;
};

export const formatDateTime = (date) => {
    if (!date) return '';
    const formatted = useDateFormat(date, 'MM/DD/YYYY hh:mm A');
    return formatted.value;
};

/**
 * Formats a number with locale-specific formatting.
 * @param {number} number - The number to format.
 * @param {string} [locale='en-US'] - The locale to use for formatting.
 * @returns {string|number} The formatted number or 0 if invalid.
 */
export const formatNumber = (number, locale = 'en-US') => {
    if (number === null || number === undefined || isNaN(number)) return 0;
    try {
        return number.toLocaleString(locale);
    } catch (e) {
        return number.toString();
    }
};

/**
 * Formats a nullable number with locale-specific formatting.
 * @param {number|null|undefined} number - The number to format.
 * @param {string} [locale='en-US'] - The locale to use for formatting.
 * @returns {string} The formatted number or an empty string if invalid.
 */
export const formatNullableNumber = (number, locale = 'en-US') => {
    if (number === null || number === undefined || isNaN(number)) return '';
    try {
        return number.toLocaleString(locale);
    } catch (e) {
        return number.toString();
    }
};

/**
 * Formats a number as currency.
 * @param {number} number - The number to format.
 * @param {Object} [options={}] - Formatting options.
 * @param {string} [options.locale='en-US'] - The locale to use.
 * @param {string} [options.currency='USD'] - The currency code.
 * @param {number} [options.minimumFractionDigits=0] - Minimum fraction digits.
 * @param {number} [options.maximumFractionDigits=0] - Maximum fraction digits.
 * @param {boolean} [options.returnNull=false] - Whether to return null for invalid input.
 * @returns {string|null} The formatted currency string or null.
 */
export const formatCurrency = (number, options = {}) => {
    const { locale = 'en-US', currency = 'USD', minimumFractionDigits = 0, maximumFractionDigits = 0, returnNull = false } = options;

    if (number === null || number === undefined || isNaN(number)) {
        return returnNull ? null : new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits, maximumFractionDigits }).format(0);
    }
    return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits, maximumFractionDigits }).format(number);
};

/**
 * Formats a number as a percentage.
 * @param {number} number - The number to format.
 * @param {Object} [options={}] - Formatting options.
 * @param {string} [options.locale='en-US'] - The locale to use.
 * @param {number} [options.minimumFractionDigits=0] - Minimum fraction digits.
 * @param {number} [options.maximumFractionDigits=0] - Maximum fraction digits.
 * @returns {string} The formatted percentage string.
 */
export const formatPercentage = (number, options = {}) => {
    const { locale = 'en-US', minimumFractionDigits = 0, maximumFractionDigits = 0 } = options;

    if (number === null || number === undefined || isNaN(number)) {
        return new Intl.NumberFormat(locale, { style: 'percent', minimumFractionDigits, maximumFractionDigits }).format(0);
    }

    if (number < 0 || number > 1) {
        number = number / 100; // Convert to decimal if it's a percentage value
    }
    
    return new Intl.NumberFormat(locale, { style: 'percent', minimumFractionDigits, maximumFractionDigits }).format(number);
};

/**
 * Formats a phone number in the format (XXX) XXX-XXXX.
 * 
 * @param {string} phoneNumber - The phone number to format.
 * @returns {string} - The formatted phone number.
 */
export const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    return phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
};

/**
 * Formats a postal code by inserting a hyphen between the first 5 digits and the next 4 digits.
 * If the input is null, undefined, or an empty string, it returns an empty string.
 *
 * @param {string} postalCode - The postal code to format. Expected to be a string of 9 digits.
 * @returns {string} The formatted postal code in the format "XXXXX-XXXX", or an empty string if the input is invalid.
 */
export const formatPostalCode = (postalCode) => {
    if (!postalCode) return '';
    return postalCode.replace(/(\d{5})(\d{4})/, '$1-$2');
};

/**
 * Formats a file size into a human-readable string.
 * @param {number} size - The file size in bytes.
 * @returns {string} The formatted file size string.
 */
export const formatFileSize = (size) => {
    if (size === null || size === undefined || isNaN(size)) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    let unitIndex = 0;
    while (size >= 1024) {
        size /= 1024;
        unitIndex++;
    }

    return size.toFixed(2) + ' ' + units[unitIndex];
};

///===============================
// DATA COMPARISON UTILITIES
// ===============================

export function isPropertyChanged(currentData, initialData, property) {
  return !_.isEqual(currentData[property], initialData[property]);
};

export function isMissingValue(value) {
  if (typeof value === 'boolean') {
    return !value;
  } else {
    return value === null || value === undefined || value === '';
  }
};

export const isSameDate = (date1, date2) => {
    if (!date1 || !date2) return false;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return (
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate()
    );
};

export const parseLocalDate = (dateString) => {
    if (!dateString) return null;

    // Extract just the date part if it's an ISO string
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    
    // Create date in local timezone
    return new Date(year, month - 1, day);
};


/**
 * Sanitizes an HTML string to remove potentially unsafe content.
 * @param {string} html - The HTML string to sanitize.
 * @returns {string} The sanitized HTML string.
 */
export const sanitizeHTML = (html) => {
    if (!html || typeof html !== 'string') return '';
    return DOMPurify.sanitize(html);
};

/**
 * Sanitizes a given input by removing all non-numeric characters.
 *
 * @param {string} value - The input string to sanitize.
 * @returns {string} A string containing only numeric characters. Returns an empty string if the input is falsy.
 */
export const sanitizeNumber = (value) => {
    if (!value) return '';
    return value.replace(/\D/g, '');
};

/**
 * Removes all spaces from the given string.
 *
 * @param {string} value - The input string from which spaces will be removed.
 * @returns {string} The input string with all spaces removed. If the input is falsy, returns an empty string.
 */
export const removeSpaces = (value) => {
    if (!value) return '';
    return value.replace(/\s/g, '');
};

/**
 * Replaces all occurrences of the word "SAINT" (case-insensitive) in the given string with "ST.".
 * If the input value is falsy, it returns an empty string.
 *
 * @param {string} value - The input string to process.
 * @returns {string} The modified string with "SAINT" replaced by "ST." or an empty string if the input is falsy.
 */
export function replaceSaintWithSt(value) {
    if (!value) return '';
    return value.replace(/SAINT/gi, 'ST.');
};


export const parseBoolean = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
        const lowerValue = value.toLowerCase();
        return lowerValue === 'true' || lowerValue === '1';
    }
    return Boolean(value);
};